/** Epic 263A / 264 — family-aware customer-facing episode ledger labels. */

type SafetyEpisodeCopyFields = {
  qualifyingEpisodes: string;
  qualifyingObservations: string;
  observedTracks: string;
  evidenceCaptured: string;
  lastObserved: string;
  activeEpisodes: string;
  timelineHeading: string;
  unavailableTitle: string;
  unavailableBody: string;
  timelineUnavailable: string;
  timelineEmpty: string;
  timelineLoading: string;
  timelineError: string;
  startedAt: string;
  qualifiedAt: string;
  endedAt: string;
  localTrackId: string;
  boundaryReason: string;
  status: string;
  evidenceRefs: string;
  forkliftNearby: string;
  confidence: string;
  legacyEvidenceWarningTitle: string;
  legacyEvidenceWarningBody: string;
};

export const SAFETY_EPISODE_COPY: SafetyEpisodeCopyFields = {
  qualifyingEpisodes: 'Qualifying entries',
  qualifyingObservations: 'Qualifying observations',
  observedTracks: 'Person-track entries',
  evidenceCaptured: 'Evidence captured',
  lastObserved: 'Last observed',
  activeEpisodes: 'Still active',
  timelineHeading: 'Qualifying entries in this observation',
  unavailableTitle: 'Qualifying entry ledger unavailable',
  unavailableBody:
    'This observation does not have a durable qualifying-entry ledger yet. Counts are not inferred from observation updates.',
  timelineUnavailable:
    'Qualifying-entry timeline is unavailable for this observation. Historical observations may not have entry records.',
  timelineEmpty: 'No qualifying entries have been recorded for this observation yet.',
  timelineLoading: 'Loading qualifying entries…',
  timelineError: 'Qualifying-entry timeline is temporarily unavailable.',
  startedAt: 'Entered',
  qualifiedAt: 'Qualified',
  endedAt: 'Left',
  localTrackId: 'Entry / track',
  boundaryReason: 'Why it counted',
  status: 'Status',
  evidenceRefs: 'Evidence refs',
  forkliftNearby: 'Forklift co-present (same camera)',
  confidence: 'Confidence',
  legacyEvidenceWarningTitle: 'Entry-level media indexing not available',
  legacyEvidenceWarningBody:
    'Entry-level media indexing was not available when this observation was captured. Media covers the observation window; individual qualifying entries may not seek to their exact qualification moments yet.',
};

const NEAR_MISS_COPY: SafetyEpisodeCopyFields = {
  ...SAFETY_EPISODE_COPY,
  qualifyingEpisodes: 'Near-miss episodes',
  qualifyingObservations: 'Qualifying observations',
  observedTracks: 'Pedestrian tracks',
  timelineHeading: 'Near-miss episodes in this review',
  unavailableTitle: 'Near-miss episode ledger unavailable',
  unavailableBody:
    'This near-miss review does not have a durable episode ledger yet. Counts are not inferred from incident updates.',
  timelineUnavailable:
    'Near-miss episode timeline is unavailable for this review. Historical notes may not have episode records.',
  timelineEmpty: 'No near-miss episodes have been recorded for this review yet.',
  timelineLoading: 'Loading near-miss episodes…',
  timelineError: 'Near-miss episode timeline is temporarily unavailable.',
  startedAt: 'Condition started',
  qualifiedAt: 'Qualified',
  endedAt: 'Cleared',
  localTrackId: 'Pedestrian track',
  legacyEvidenceWarningTitle: 'Episode-level media indexing not available',
  legacyEvidenceWarningBody:
    'Episode-level media indexing was not available when this near-miss was captured. Media may cover the review window; qualification moments may not seek yet.',
};

export type SafetyEpisodeCopy = SafetyEpisodeCopyFields;

export function resolveSafetyEpisodeCopy(
  incidentType?: string | null,
  episodeFamily?: string | null,
): SafetyEpisodeCopy {
  const family = String(episodeFamily || '').trim().toLowerCase();
  const token = String(incidentType || '').trim().toLowerCase();
  if (
    family === 'near_miss'
    || (!family && token === 'near_miss')
    || (!family && token.includes('forklift_proximity'))
    || (!family && token.includes('pedestrian_near_moving_forklift'))
  ) {
    return NEAR_MISS_COPY;
  }
  return SAFETY_EPISODE_COPY;
}

/** Customer-facing reason View moment is disabled for a non-indexed status. */
export function viewMomentUnavailableCopy(
  evidenceStatus?: string | null,
): string | null {
  switch (String(evidenceStatus || '').trim().toLowerCase()) {
    case 'media_pending':
      return 'Clip is still being prepared for this qualification moment.';
    case 'outside_clip_window':
      return 'Available clip does not cover this qualification moment.';
    case 'media_unavailable':
      return 'No clip is available for this qualification moment.';
    case 'legacy_unindexed':
      return 'This entry was captured before moment indexing was available.';
    default:
      return null;
  }
}
